import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";
import { Layout, Menu, message } from "antd";
import {
  DashboardOutlined,
  LogoutOutlined,
  UserOutlined,
  GiftOutlined,
  HomeOutlined,
  SettingOutlined,
  HistoryOutlined,
  BgColorsOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardPage from "./pages/Dashboard";
import AppearancePage from "./pages/Appearance";
import SettingsPage from "./pages/Settings";
import LoginForm from "./components/LoginForm";
import logo from "./assets/logo.png";
import ForgotPasswordPage from "./pages/ForgotPassword";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetPasswordPage from "./pages/SetPassword";
import { API_BASE_URL } from "./api/api";
import UserPage from "./pages/UserPage";
import VacationOrdersPage from "./pages/VacationOrdersPage";
import HotelOrdersPage from "./pages/HotelOrdersPage";
import HotelOrderDetailPage from "./pages/HotelOrderDetailPage";
import PointsEarnedPage from "./pages/PointsEarnedPage";
import PointsRedeemedPage from "./pages/PointsRedeemedPage";
import PointsCashOutPage from "./pages/PointsCashOutPage";
import PointsActionLogPage from "./pages/PointsActionLogPage";
import VacationOrderDetailPage from "./pages/VacationOrderDetailPage";
import UserDetailPage from "./pages/UserDetailPage";
import AgencyPage from "./pages/AgencyPage";
import RateConfigPage from "./pages/rateConfigPage";
import SpecialRateConfigPage from "./pages/specialRateConfigPage";
import DestinationManagementPage from "./pages/DestinationManagementPage";
import DestinationEditorPage from "./pages/DestinationEditorPage";
import DealManagementPage from "./pages/DealManagementPage";
import DealEditorPage from "./pages/DealEditorPage";

const { Header, Sider, Content } = Layout;
const CRISP_WEBSITE_ID = "484e8360-7e33-48bd-83d6-2de82161e42e";

const getStoredUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("userInfo") || "{}");
  } catch (error) {
    console.error("Invalid userInfo in localStorage", error);
    return {};
  }
};

const DashboardLayout = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const userRole = userInfo.role_id;
  const selectedMenuKey = location.pathname.split("/")[1] || "dashboard";
  const defaultRoute = userRole === 4 ? "/destinations" : "/dashboard";

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleMenuClick = (e) => {
    if (e.key === "logout") {
      onLogout();
    } else {
      navigate(`/${e.key}`);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        theme="light"
        collapsible
        collapsed={collapsed}
        trigger={null}
        style={{ position: "relative" }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "16px",
          }}
        >
          <img
            src={logo}
            alt="Company Logo"
            style={{
              width: collapsed ? "40px" : "120px",
              transition: "width 0.3s ease",
            }}
          />
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          onClick={handleMenuClick}
          style={{ flex: 1 }}
        >
          {userRole !== 4 && (
            <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
              Dashboard
            </Menu.Item>
          )}
          {[1, 2].includes(userRole) && (
            <Menu.Item key="user" icon={<UserOutlined />}>
              <Link to="/user">User</Link>
            </Menu.Item>
          )}
          {[1, 2].includes(userRole) && (
            <Menu.SubMenu
              key="orders"
              icon={<HistoryOutlined />}
              title="Orders"
            >
              <Menu.Item key="vacation-orders">Vacation Package</Menu.Item>
              <Menu.Item key="hotel-orders">Hotel</Menu.Item>
            </Menu.SubMenu>
          )}
          {[3].includes(userRole) && (
            <Menu.Item key="vacation-orders" icon={<HistoryOutlined />}>
              Vacation Package
            </Menu.Item>
          )}
          {[1, 2].includes(userRole) && (
            <Menu.SubMenu
              key="membership-points"
              icon={<GiftOutlined />}
              title="Points"
            >
              <Menu.Item key="points-earned">Earned</Menu.Item>
              <Menu.Item key="points-redeemed">Redeemed</Menu.Item>
              <Menu.Item key="points-cash-out">Cash Out</Menu.Item>
              <Menu.Item key="points-action-log">Action Log</Menu.Item>
            </Menu.SubMenu>
          )}
          {[1, 2].includes(userRole) && (
            <Menu.SubMenu
              key="membership-config"
              icon={<SettingOutlined />}
              title="Configurations"
            >
              <Menu.Item key="rate-config">Basic Config</Menu.Item>
              <Menu.Item key="special-rate-config">Special Config</Menu.Item>
            </Menu.SubMenu>
          )}
          {[1, 2].includes(userRole) && (
            <Menu.Item key="appearance" icon={<BgColorsOutlined />}>
              Appearance (Demo)
            </Menu.Item>
          )}
          {[3].includes(userRole) && (
            <Menu.Item key="appearance" icon={<BgColorsOutlined />}>
              Appearance
            </Menu.Item>
          )}
          {[1, 2].includes(userRole) && (
            <Menu.Item key="agency" icon={<TeamOutlined />}>
              Agency
            </Menu.Item>
          )}
          {[1, 2, 4].includes(userRole) && (
            <Menu.Item key="destinations" icon={<EnvironmentOutlined />}>
              Destination Management
            </Menu.Item>
          )}
          {[1, 2].includes(userRole) && (
            <Menu.Item key="deals" icon={<TagsOutlined />}>
              Tour Operator Deals
            </Menu.Item>
          )}
          <Menu.Item key="logout" icon={<LogoutOutlined />}>
            Logout
          </Menu.Item>
        </Menu>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            padding: "10px 0",
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            cursor: "pointer",
          }}
          onClick={toggleCollapsed}
        >
          <HomeOutlined style={{ fontSize: 18 }} />
        </div>
      </Sider>
      <Layout>
        <Header style={{ background: "#fff", paddingLeft: 20 }}>
          Welcome Back!
        </Header>
        <Content style={{ margin: "16px" }}>
          <div style={{ padding: 24, background: "#fff" }}>
            <Routes>
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <DashboardPage />{" "}
                  </ProtectedRoute>
                }
              />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/vacation-orders" element={<VacationOrdersPage />} />
              <Route
                path="/vacation-orders/:order_id"
                element={<VacationOrderDetailPage />}
              />
              <Route
                path="/points-earned"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <PointsEarnedPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/points-redeemed"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <PointsRedeemedPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/points-cash-out"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <PointsCashOutPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/points-action-log"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <PointsActionLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hotel-orders"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <HotelOrdersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hotel-orders/:bookingId"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <HotelOrderDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="*" element={<Navigate to={defaultRoute} />} />
              <Route
                path="/user"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <UserPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user/:memberId"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <UserDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rate-config"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <RateConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/special-rate-config"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <SpecialRateConfigPage />{" "}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appearance"
                element={
                  <ProtectedRoute allowedRoles={[1, 2, 3]}>
                    <AppearancePage />{" "}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <AgencyPage />{" "}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/destinations"
                element={
                  <ProtectedRoute allowedRoles={[1, 2, 4]}>
                    <DestinationManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/destinations/new"
                element={
                  <ProtectedRoute allowedRoles={[1, 2, 4]}>
                    <DestinationEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/destinations/:destinationId"
                element={
                  <ProtectedRoute allowedRoles={[1, 2, 4]}>
                    <DestinationEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <DealManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/new"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <DealEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/:dealId"
                element={
                  <ProtectedRoute allowedRoles={[1, 2]}>
                    <DealEditorPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoutTimer, setLogoutTimer] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          handleLogout();
        } else {
          setIsLoggedIn(true);
          startAutoLogoutTimer(decoded.exp);
        }
      } catch (err) {
        console.error("Invalid token", err);
        handleLogout();
      }
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      if (window.$crisp) {
        window.$crisp.push(["do", "session:reset"]);
        window.$crisp.push(["do", "chat:hide"]);
      }
      return;
    }

    const userInfo = getStoredUserInfo();
    const email = userInfo.email;
    const nickname = [userInfo.first_name, userInfo.last_name]
      .filter(Boolean)
      .join(" ");
    const sessionData = [
      ["crm_user_id", String(userInfo.id || "")],
      ["role_id", String(userInfo.role_id || "")],
    ].filter(([, value]) => value);

    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

    if (email) {
      window.$crisp.push(["set", "user:email", [email]]);
    }

    if (nickname) {
      window.$crisp.push(["set", "user:nickname", [nickname]]);
    }

    if (sessionData.length > 0) {
      window.$crisp.push(["set", "session:data", [sessionData]]);
    }

    window.$crisp.push(["do", "chat:show"]);

    const existingScript = document.getElementById("crisp-chat-script");
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = "crisp-chat-script";
      script.src = "https://client.crisp.chat/l.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, [isLoggedIn]);

  const startAutoLogoutTimer = (exp) => {
    const expirationTimeMs = exp * 1000 - Date.now();
    if (expirationTimeMs > 0) {
      const timer = setTimeout(() => {
        handleLogout();
        message.info("Session expired, please login again");
      }, expirationTimeMs);
      setLogoutTimer(timer);
    }
  };

  const clearAutoLogoutTimer = () => {
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      setLogoutTimer(null);
    }
  };

  const onLogin = ({ token, user }) => {
    localStorage.setItem("token", token);
    localStorage.setItem("userInfo", JSON.stringify(user));
    setIsLoggedIn(true);
    const decoded = jwtDecode(token);
    startAutoLogoutTimer(decoded.exp);
  };

  const handleLogout = () => {
    clearAutoLogoutTimer();
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");
    setIsLoggedIn(false);
    message.success("Logged out");
  };

  return (
    <Router>
      {isLoggedIn ? (
        <DashboardLayout onLogout={handleLogout} />
      ) : (
        <Routes>
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route
            path="*"
            element={<LoginForm onLogin={onLogin} loading={loading} />}
          />
        </Routes>
      )}
    </Router>
  );
}

export default App;
